import React from "react";
import { motion } from "framer-motion";
import { Calendar, AlertTriangle, X, CreditCard } from "lucide-react";
import { trackScheduledPaymentCancelled } from "../../utils/posthogTracking";

const ModalConfirmCancelScheduled = (props) => {
  const { onConfirm, onCancel, scheduledDate, amount, methodDesc, payments } = props.modalProps || {};

  const isPaymentPlan = payments && payments.length > 1;
  const planTotal = isPaymentPlan ? payments.reduce((sum, p) => sum + p.amount, 0) : amount;

  const formatMoney = (num) => {
    return Number(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Format date in UTC to avoid timezone shifting
  const formatDateUTC = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const handleConfirm = () => {
    // Track scheduled payment cancellation
    trackScheduledPaymentCancelled(planTotal, scheduledDate);

    if (onConfirm) onConfirm();
    if (onCancel) onCancel();
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  const contentAnimation = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, staggerChildren: 0.1 } },
  };

  return (
    <div className="flex flex-col h-full max-h-[100vh] sm:max-h-[90vh] p-0">
      <div className="relative bg-gradient-to-r from-red-600 to-red-700 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 flex-shrink-0 sm:rounded-t-2xl">
        <button onClick={handleCancel} className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group z-10">
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
        </button>
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="flex items-center gap-3 sm:gap-4 pr-10">
          <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            {isPaymentPlan ? (
              <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
            ) : (
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
              {isPaymentPlan ? "Cancel Payment Plan" : "Cancel Scheduled Payment"}
            </h2>
            <p className="text-red-100 text-sm sm:text-base">This action cannot be undone</p>
          </div>
        </motion.div>
      </div>

      <motion.div className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 min-h-0 overscroll-contain bg-gray-900" variants={contentAnimation} initial="hidden" animate="visible">
        {isPaymentPlan ? (
          // Payment plan view
          <>
            <div className="mb-4 p-4 bg-white/[0.03] rounded-xl border border-white/10">
              <div className="text-sm text-white/80 mb-2">
                This plan includes {payments.length} scheduled payments:
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {payments.map((payment) => (
                  <div key={payment.id} className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
                    <span className="text-white/90 text-sm">
                      {formatDateUTC(payment.scheduled || payment.date)}
                    </span>
                    <span className="text-white font-medium text-sm">
                      ${formatMoney(payment.amount)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-3 mt-2 border-t border-white/10">
                <span className="text-white font-semibold">Total</span>
                <span className="text-white font-bold text-lg">${formatMoney(planTotal)}</span>
              </div>
            </div>

            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex gap-3 mb-6">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-300 font-medium mb-1">Important Disclaimer</p>
                <p className="text-sm text-yellow-200/80">
                  If you cancel this payment plan, we are not obligated to offer a similar payment plan in the future.
                  Future payment arrangements may have different terms, payment amounts, or may not be available at all.
                </p>
              </div>
            </div>
          </>
        ) : (
          // Single payment view
          <>
            <div className="mb-4 p-4 bg-white/[0.03] rounded-xl border border-white/10">
              <div className="text-sm text-white/80">Scheduled Date</div>
              <div className="text-white font-semibold">{scheduledDate}</div>
              {methodDesc && <div className="mt-2 text-sm text-white/80">Method</div>}
              {methodDesc && <div className="text-white">{methodDesc}</div>}
              {amount !== undefined && <div className="mt-2 text-sm text-white/80">Amount</div>}
              {amount !== undefined && <div className="text-white">${amount}</div>}
            </div>

            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex gap-3 mb-6">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-300 font-medium mb-1">Important</p>
                <p className="text-sm text-yellow-200/80">Cancelling will remove this scheduled payment and it will not be processed on the scheduled date.</p>
              </div>
            </div>
          </>
        )}

        <div className="flex gap-3">
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="button" onClick={handleCancel} className="flex-1 py-3 px-6 bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 text-white font-semibold rounded-xl transition-all">
            {isPaymentPlan ? "Keep Plan" : "Keep Scheduled"}
          </motion.button>
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="button" onClick={handleConfirm} className="flex-1 py-3 px-6 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white font-semibold rounded-xl shadow-lg shadow-red-500/25 transition-all">
            {isPaymentPlan ? "Cancel Plan" : "Cancel Payment"}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default ModalConfirmCancelScheduled;
